/**
 * The moments the game makes a sound, by the names the sound library maps them to.
 *
 * Shared, because the world names some of them (a swing, an axe biting) and the
 * client names the rest (a footstep, a button), and the tools list them all so
 * each can be given sounds. A cue with no sounds given is silent, which is what
 * a world with an empty library sounds like.
 */

export type SoundGroup =
  'steps' | 'fighting' | 'skills' | 'items' | 'world' | 'interface' | 'ambient';

export interface SoundCue {
  /** What the library and the world call it. */
  cue: string;
  /** What the tools call it. */
  label: string;
  group: SoundGroup;
  /** Played over and over while it applies rather than once. */
  loops?: boolean;
}

export const SOUND_GROUPS: readonly { group: SoundGroup; label: string }[] = [
  { group: 'steps', label: 'Footsteps' },
  { group: 'fighting', label: 'Fighting' },
  { group: 'skills', label: 'Skills' },
  { group: 'items', label: 'Items' },
  { group: 'world', label: 'The world' },
  { group: 'interface', label: 'Interface' },
  { group: 'ambient', label: 'Background' },
];

export const SOUND_CUES: readonly SoundCue[] = [
  { cue: 'step-grass', label: 'On grass', group: 'steps' },
  { cue: 'step-dirt', label: 'On dirt and paths', group: 'steps' },
  { cue: 'step-sand', label: 'On sand', group: 'steps' },
  { cue: 'step-rock', label: 'On rock and ash', group: 'steps' },
  { cue: 'step-snow', label: 'On snow', group: 'steps' },
  { cue: 'step-wood', label: 'On boards', group: 'steps' },
  { cue: 'step-water', label: 'Wading', group: 'steps' },

  { cue: 'swing', label: 'A melee swing', group: 'fighting' },
  { cue: 'shoot', label: 'An arrow loosed', group: 'fighting' },
  { cue: 'hit', label: 'A blow that lands', group: 'fighting' },
  { cue: 'block', label: 'A blow that does nothing', group: 'fighting' },
  { cue: 'hurt', label: 'Being hurt yourself', group: 'fighting' },
  { cue: 'npc-die', label: 'Something dying', group: 'fighting' },
  { cue: 'death', label: 'Dying yourself', group: 'fighting' },
  { cue: 'cast', label: 'A spell cast, when it has none of its own', group: 'fighting' },
  { cue: 'impact', label: 'A spell landing, when it has none of its own', group: 'fighting' },

  { cue: 'chop', label: 'Chopping', group: 'skills' },
  { cue: 'mine', label: 'Mining', group: 'skills' },
  { cue: 'fish', label: 'Fishing', group: 'skills' },
  { cue: 'pick', label: 'Picking', group: 'skills' },
  { cue: 'cook', label: 'Cooking', group: 'skills' },
  { cue: 'smelt', label: 'Smelting', group: 'skills' },
  { cue: 'smith', label: 'Smithing', group: 'skills' },
  { cue: 'light', label: 'Lighting a fire', group: 'skills' },
  { cue: 'craft', label: 'Making anything else', group: 'skills' },
  { cue: 'level-up', label: 'A level gained', group: 'skills' },

  { cue: 'pick-up', label: 'Picking something up', group: 'items' },
  { cue: 'drop', label: 'Dropping something', group: 'items' },
  { cue: 'eat', label: 'Eating', group: 'items' },
  { cue: 'equip', label: 'Putting something on', group: 'items' },
  { cue: 'coins', label: 'Coins changing hands', group: 'items' },

  { cue: 'teleport', label: 'Arriving by magic', group: 'world' },
  { cue: 'prayer-on', label: 'A prayer taken up', group: 'world' },
  { cue: 'prayer-off', label: 'A prayer let go', group: 'world' },
  { cue: 'bank', label: 'A bank opened', group: 'world' },

  { cue: 'click', label: 'A button pressed', group: 'interface' },
  { cue: 'open', label: 'A panel opened', group: 'interface' },
  { cue: 'close', label: 'A panel closed', group: 'interface' },
  { cue: 'refused', label: 'Something that cannot be done', group: 'interface' },
  { cue: 'message', label: 'A private message', group: 'interface' },

  { cue: 'ambient-day', label: 'By day', group: 'ambient', loops: true },
  { cue: 'ambient-night', label: 'By night', group: 'ambient', loops: true },
  { cue: 'ambient-sea', label: 'Near the sea', group: 'ambient', loops: true },
];

const CUE_NAMES = new Set(SOUND_CUES.map((one) => one.cue));

export function isSoundCue(name: string): boolean {
  return CUE_NAMES.has(name);
}

/** Which footstep a kind of ground makes, by the ground names the regions use. */
export function stepCue(ground: string, wading: boolean, onBoards: boolean): string {
  if (wading) return 'step-water';
  if (onBoards) return 'step-wood';
  switch (ground) {
    case 'sand':
      return 'step-sand';
    case 'dirt':
    case 'path':
      return 'step-dirt';
    case 'rock':
    case 'ash':
    case 'lava':
      return 'step-rock';
    case 'snow':
      return 'step-snow';
    default:
      return 'step-grass';
  }
}

/** Which skill cue a gathering skill makes each time it is tried. */
export function gatherCue(skill: string): string {
  switch (skill) {
    case 'woodcutting':
      return 'chop';
    case 'mining':
      return 'mine';
    case 'fishing':
      return 'fish';
    default:
      return 'pick';
  }
}

/** Which skill cue making something makes, by the skill and the action that started it. */
export function craftCue(skill: string, action: string): string {
  if (/smelt/i.test(action)) return 'smelt';
  switch (skill) {
    case 'cooking':
      return 'cook';
    case 'smithing':
      return 'smith';
    case 'firemaking':
      return 'light';
    default:
      return 'craft';
  }
}
